import React from 'react';

const bodySignals = [
    {
        title: 'Heart',
        detail: 'Heart rate & HRV',
        points: '0,13 10,11 20,14 30,9 40,12 50,8 60,11 70,7 80,10',
    },
    {
        title: 'Sleep',
        detail: 'Sleep & recovery',
        points: '0,11 10,12 20,11 30,12 40,10 50,11 60,9 70,10 80,8',
    },
    {
        title: 'Movement',
        detail: 'Activity & motion',
        points: '0,14 10,8 20,12 30,5 40,11 50,7 60,13 70,6 80,10',
    },
];

const mindStates = [
    'Deeply focused',
    'Working hard',
    'Ready to learn',
    'Time for a break',
];

const Problem = () => {
    return (
        <section
            id="gap"
            className="relative overflow-hidden bg-[#080a0f] py-20 text-white sm:py-24"
        >
            {/* Background atmosphere */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/2 top-1/3 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#3157ff]/[0.035] blur-[150px]" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1400px] px-8">

                {/* Section heading */}
                <div className="max-w-[850px]">

                    <div className="mb-6 flex items-center gap-3 text-[11px] tracking-[0.28em] text-neutral-500">
                        <span className="h-px w-8 bg-neutral-600" />
                        THE MISSING LAYER
                    </div>

                    <h2 className="text-[42px] font-semibold leading-[1.02] tracking-[-0.04em] sm:text-[54px] lg:text-[64px]">

                        {/* Current state */}
                        <span className="text-neutral-500">
                            Your body has been measured.
                        </span>

                        <br />

                        {/* FroCort opportunity */}
                        <span className="text-white">
                            Your mind is next.
                        </span>

                    </h2>

                    <p className="mt-6 max-w-[680px] text-[16px] leading-[1.7] text-neutral-400 sm:text-[17px]">
                        Today’s wearables can tell you a lot about your body.
                        But much of what is happening in the mind remains
                        difficult to observe from the outside.
                    </p>

                </div>

                {/* Body → Mind visual */}
                <div className="mt-12 grid items-stretch gap-4 lg:grid-cols-[1fr_auto_1fr] lg:gap-7">

                    {/* Existing signals */}
                    <div className="rounded-[26px] border border-white/10 bg-[#0b0d13] p-7 sm:p-8">

                        <div className="mb-6 text-[10px] uppercase tracking-[0.24em] text-neutral-600">
                            What wearables measure today
                        </div>

                        <div className="space-y-2.5">

                            {bodySignals.map((signal) => (
                                <div
                                    key={signal.title}
                                    className="group flex items-center gap-5 rounded-2xl border border-white/[0.08] bg-white/[0.018] px-5 py-4 transition-colors duration-500 hover:border-white/[0.14]"
                                >

                                    {/* Signal visual */}
                                    <div className="relative h-8 w-[92px] shrink-0 overflow-hidden opacity-40 transition-opacity duration-500 group-hover:opacity-70">

                                        <svg
                                            viewBox="0 0 80 20"
                                            preserveAspectRatio="none"
                                            className="h-full w-full"
                                        >
                                            <polyline
                                                points={signal.points}
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="text-neutral-500"
                                            />
                                        </svg>

                                    </div>

                                    {/* Signal name */}
                                    <div className="min-w-0">

                                        <div className="text-[14px] font-medium text-neutral-400">
                                            {signal.title}
                                        </div>

                                        <div className="mt-1 text-[11px] text-neutral-600">
                                            {signal.detail}
                                        </div>

                                    </div>

                                    {/* Status */}
                                    <div className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-700" />

                                </div>
                            ))}

                        </div>

                        <p className="mt-6 max-w-[440px] text-[12px] leading-5 text-neutral-600">
                            Valuable physiological signals — but they do not
                            directly tell the device what is happening
                            cognitively.
                        </p>

                    </div>

                    {/* Connector */}
                    <div className="hidden lg:flex items-center justify-center">

                        <div className="flex flex-col items-center">

                            <div className="h-16 w-px bg-gradient-to-b from-transparent via-white/10 to-white/20" />

                            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#8ea5ff]/20 bg-[#10162a]">

                                <span className="text-[15px] text-[#8ea5ff]">
                                    →
                                </span>

                            </div>

                            <div className="h-16 w-px bg-gradient-to-b from-white/20 via-white/10 to-transparent" />

                        </div>

                    </div>

                    {/* Missing cognitive layer */}
                    <div className="relative overflow-hidden rounded-[26px] border border-[#8ea5ff]/20 bg-[#0d1220] p-7 sm:p-8">

                        {/* Subtle glow */}
                        <div className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full bg-[#3157ff]/[0.08] blur-[100px]" />

                        <div className="relative">

                            <div className="mb-6 flex items-center justify-between">

                                <div className="text-[10px] uppercase tracking-[0.24em] text-[#8ea5ff]">
                                    What FroCort is building toward
                                </div>

                                <div className="flex items-center gap-2">

                                    <span className="text-[9px] tracking-[0.16em] text-neutral-600">
                                        COGNITIVE
                                    </span>

                                    <span className="h-1.5 w-1.5 rounded-full bg-[#8ea5ff] shadow-[0_0_9px_rgba(142,165,255,0.7)]" />

                                </div>

                            </div>

                            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">

                                {mindStates.map((state, index) => (
                                    <div
                                        key={state}
                                        className="group relative overflow-hidden rounded-2xl border border-[#8ea5ff]/15 bg-[#8ea5ff]/[0.035] px-5 py-4 transition-all duration-500 hover:border-[#8ea5ff]/30 hover:bg-[#8ea5ff]/[0.07]"
                                    >

                                        <div className="flex items-center justify-between gap-4">

                                            <span className="text-[14px] text-neutral-200">
                                                {state}
                                            </span>

                                            <span
                                                className={`h-1.5 w-1.5 shrink-0 rounded-full transition-all duration-500 ${
                                                    index === 0
                                                        ? 'bg-[#8ea5ff]/70 group-hover:bg-[#8ea5ff] group-hover:shadow-[0_0_8px_rgba(142,165,255,0.7)]'
                                                        : 'bg-[#8ea5ff]/20 group-hover:bg-[#8ea5ff]/60'
                                                }`}
                                            />

                                        </div>

                                    </div>
                                ))}

                            </div>

                            <p className="mt-6 max-w-[520px] text-[12px] leading-5 text-neutral-500">
                                These are target cognitive experiences
                                FroCort is working toward detecting and
                                validating through measurable signals and
                                human studies.
                            </p>

                        </div>

                    </div>

                </div>

                {/* Section conclusion */}
                <div className="mt-10 border-t border-white/10 pt-7">

                    <p className="max-w-[1000px] text-[20px] font-medium leading-[1.45] tracking-[-0.02em] text-neutral-500 sm:text-[24px]">

                        <span className="text-neutral-500">
                            Wearables already understand parts of your body.
                        </span>

                        {' '}

                        <span className="text-white">
                            FroCort is building toward the missing layer:
                            understanding how you are doing.
                        </span>

                    </p>

                </div>

            </div>
        </section>
    );
};

export default Problem;